<template>
  <div class="container-fluid p-0 table-fix-head" id="content-page-table" :style="fixHeadCalculatedStyle">
    <table v-if="uiOrders.length > 0" class="table table-hover table-borderless table-sm table-fit align-middle">
      <thead>
        <!-- Show on large screen -->
        <tr class="d-none d-sm-table-row">
          <th>{{ $t('field.generic.order') }}</th>
          <th class="d-none d-lg-table-cell">{{ $t('field.generic.supplier') }}</th>
          <th>
            {{ $t('field.order.deliveryDate') }}
          </th>
          <th>{{ $t('field.generic.status') }}</th>
          <th>
            {{ $t('field.generic.created') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th class="d-none d-lg-table-cell">{{ $t('field.generic.linesCount') }}</th>
          <th>{{ $t('field.generic.rowActions') }}</th>
        </tr>
        <!-- Show on small screen -->
        <tr class="d-table-row d-sm-none">
          <th>{{ $t('field.generic.order') }}</th>
          <th>
            {{ $t('field.generic.status') }} /<br>
            {{ $t('field.order.deliveryDate') }}
          </th>
          <th>
            {{ $t('field.generic.created') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th>{{ $t('field.generic.rowActions') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="entry in uiOrders" :key="entry.id">
          <!-- Show on large screen -->
          <tr class="d-none d-sm-table-row item-row-one">
            <td @click="emit('selected', entry.id)">
              <button class="btn btn-link text-start ps-0" type="button">{{ entry.code }}</button>
              <span class="text-melodi d-inline d-lg-none"><br>{{ entry.supplier.name }}</span>
            </td>
            <td class="d-none d-lg-table-cell">
              <span class="text-melodi">{{ entry.supplier.name }}</span>
            </td>
            <td>
              <span v-if="entry.deliveryDate" :class="{ 'text-body-tertiary opacity-50': entry.supplier.info.autoConfirmOrder }" :title="entry.supplier.info.autoConfirmOrder ? $t('field.supplier.autoConfirmOrderTitle') : undefined">
                {{ $d(new Date(entry.deliveryDate), 'dateonly') }}
              </span>
            </td>
            <td>
              <OrderStatus :status="entry.status" class="text-end" />
              <OrderCompleteReason :reason="entry.completeReason" :show-text="false" class="text-end ms-2" />
            </td>
            <AuditCreatedField :audit="entry.audit" />
            <td class="d-none d-lg-table-cell">{{ entry.lines.length }}</td>
            <td>
              <button v-if="userSettingsStore.data.developerMode && entry.status === ORDER_HEAD_STATUS.pending" class="btn btn-outline-danger" type="button" @click="removeOrder(entry.id)">
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
            </td>
          </tr>
          <!-- Show on small screen -->
          <tr class="d-table-row d-sm-none item-row-one">
            <td @click="emit('selected', entry.id)" class="pb-0">
              <button class="btn btn-link text-start ps-0 pb-0" type="button">{{ entry.code }}</button><br>
              <span class="text-melodi">{{ entry.supplier.name }}</span>
            </td>
            <td class="pb-0">
              <OrderStatus :status="entry.status" /><br>
              <OrderCompleteReason :reason="entry.completeReason" /><br>
              <span v-if="entry.deliveryDate" :class="{ 'text-body-tertiary opacity-50': entry.supplier.info.autoConfirmOrder }" :title="entry.supplier.info.autoConfirmOrder ? $t('field.supplier.autoConfirmOrderTitle') : undefined">
                {{ $d(new Date(entry.deliveryDate), 'dateonly') }}
              </span>
            </td>
            <AuditCreatedField :audit="entry.audit" class="pb-0" />
            <td class="pb-0">
              <button v-if="userSettingsStore.data.developerMode && entry.status === ORDER_HEAD_STATUS.pending" class="btn btn-outline-danger" type="button" @click="removeOrder(entry.id)">
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useOrdersStore, useUserSettingsStore } from '@/stores';
import { AuditCreatedField } from "@/components/fields";
import OrderStatus from '@/components/orders/OrderStatus.vue';
import OrderCompleteReason from '@/components/orders/OrderCompleteReason.vue';
import { ORDER_HEAD_STATUS, OrderHeadStatus } from '@/models/Enums';

const props = defineProps({
  statuses: { type: Array as () => OrderHeadStatus[], required: false, default: () => [] },
  showPast: { type: Boolean, required: false, default: false },
  hideReceivedByDeliveryInterface: { type: Boolean, required: false, default: false }
});

const emit = defineEmits<{
  (e: 'selected', id: number): void
}>();

const ordersStore = useOrdersStore();
const userSettingsStore = useUserSettingsStore();
const offsetTop = ref(0);

onMounted(async () => {
  await fetchOrders();
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

watch(props, async () => {
  await fetchOrders();
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const { data: orders } = storeToRefs(ordersStore);

const fetchOrders = async () => {
  await ordersStore.fetchAll(props.showPast, props.statuses);
};

const uiOrders = computed(() => {
  return [...orders.value]
    .filter(order => !props.hideReceivedByDeliveryInterface || !order.supplier.receivesByDeliveryInterface)
    .sort((a, b) => {
      return a.audit.createdAt < b.audit.createdAt ? -1 : 1;
    });
});

const removeOrder = async (id: number) => {
  await ordersStore.delete(id);
};
</script>

<style scoped lang="scss">
tr.item-row-one {
  border-top: 1px dashed #ccc;

  &:first-child {
    border-top: none;
  }
}
</style>
